import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import uniqueId from 'lodash/uniqueId'

import { useDropzone } from 'react-dropzone'

import { useNotification } from '@nfinite/react-hubstairs/components/Notification'

import { useMutation } from 'requests'
import { UPLOAD_FILE_CLOUDINARY, UPLOAD_FILE_SIGNED_URL, UPLOAD_IMAGE_FILE } from './mutation'
import translations from './translations'
import { DEFAULT_ACCEPT, IMAGE_TYPE, acceptString } from 'utils/uploads'
import heic2any from 'heic2any'

/*
 * As the error Enum is not accessible we re-created it
 * source: https://github.com/react-dropzone/react-dropzone/blob/master/src/utils/index.js#L4
 */
export const DROPZONE_ERROR_CODE = {
  FILE_INVALID_TYPE: 'file-invalid-type',
  FILE_TOO_LARGE: 'file-too-large',
  TOO_MANY_FILES: 'too-many-files',
}

const DEFAULT_MAX_SIZE = 10000000000 // 15Go

const JPEG_IMAGE_TYPES = [IMAGE_TYPE.JPEG, IMAGE_TYPE.JPG, IMAGE_TYPE.PJPEG]

const IMAGE_TYPE_REQUIRING_CONVERSION_OPTIONS = [
  ...JPEG_IMAGE_TYPES,
  IMAGE_TYPE.PNG,
  IMAGE_TYPE.AVIF,
  IMAGE_TYPE.BMP,
  IMAGE_TYPE.TIFF,
  IMAGE_TYPE.GIF,
]

async function getVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const { type } = file
    const video = document.createElement('video')
    video.addEventListener('loadedmetadata', event => {
      const { videoWidth, videoHeight, duration } = video
      const metadata = {
        width: videoWidth,
        height: videoHeight,
        duration,
        format: type,
      }
      resolve(metadata)
      video.pause()
      video.removeAttribute('src')
      video.load()
      video.remove()
    })
    video.preload = 'metadata'
    video.src = URL.createObjectURL(file)
  })
}

/**
 * If file is heic convert it to jpeg
 */
async function getFileConverted(file) {
  const { type, name } = file.file
  // TEMPORARY
  const conversionQuality = 0.5
  if (type.match('heic')) {
    const fileToBlob = async f => new Blob([new Uint8Array(await f.arrayBuffer())], { type: f.type })
    const blob = await fileToBlob(file.file)
    const fileAsJpeg = await heic2any({ blob, toType: 'image/jpeg', quality: conversionQuality }).then(
      conversionResult => {
        // conversionResult is a BLOB
        return new File([conversionResult], name.replace(/\.heic$/i, '.jpeg'))
      },
    )

    return { ...file, file: fileAsJpeg }
  }
  return file
}

function useUploadDrop({
  onUploadStart,
  onUploadComplete,
  onUploadFailed,
  onUploadRejected,
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  multiple = false,
  disabled,
  apiPath,
  customFileValidator,
  signed = false,
  uniqueUploadId,
  notifyOnReject = true,
  noClick = false,
  includeCreatedAt = false,
  getFilesFromEvent,
  customErrorMessage = false,
  limit = false,
  conversionOptions,
  includeCustomRejectedFiles = false,
  maxFiles,
}) {
  const { formatMessage } = useIntl()
  const { pushError } = useNotification()
  const [uploadSigned, { loading: loadingSigned }] = useMutation(UPLOAD_FILE_SIGNED_URL, {
    context: { onDownloadProgress, apiPath },
  })
  const [uploadCloudinary, { loading: loadingCloudinary }] = useMutation(UPLOAD_FILE_CLOUDINARY, {
    context: { onDownloadProgress },
  })
  const [uploadImageFile, { loading: loadingImageFile }] = useMutation(UPLOAD_IMAGE_FILE, {
    context: { onDownloadProgress, apiPath },
  })
  const loading = loadingSigned || loadingCloudinary || loadingImageFile

  const [percent, setPercent] = useState()

  function onDownloadProgress(progress) {
    const { percent } = progress
    setPercent(Number(percent * 100) ?? 0)
  }

  const handleUploadSigned = useCallback(
    async ({ file, uniqueUploadId }) => {
      const { type, size, width, height } = file
      const isVideo = type.match('video.*')
      const videoMetadata = isVideo ? await getVideoMetadata(file) : {}

      const { data: dataUpload, error } = await uploadSigned({ variables: { files: [file] } })

      if (dataUpload) {
        const { files } = dataUpload.uploadFileSigned
        const [signedFile] = files
        const { createdAt, ...rest } = signedFile

        const data = {
          ...rest,
          size,
          width,
          height,
          ...videoMetadata,
          ...(includeCreatedAt && { createdAt }),
        }

        return onUploadComplete({ uniqueUploadId, data })
      } else {
        onUploadFailed?.({ uniqueUploadId, error })
      }
    },
    [uploadSigned, includeCreatedAt, onUploadComplete, onUploadFailed],
  )

  const handleUploadImageFile = useCallback(
    async ({ file, uniqueUploadId }) => {
      const { size, width, height, type } = file

      const { data: dataUpload, error } = await uploadImageFile({
        variables: {
          files: [file],
          options: {
            ...(conversionOptions.autoConversion
              ? {
                  format: { name: JPEG_IMAGE_TYPES.includes(type) ? 'jpg' : 'png' },
                }
              : {}),
          },
        },
      })

      if (dataUpload) {
        const { files } = dataUpload.uploadImageFile
        const [imageFile] = files
        const { createdAt, ...rest } = imageFile

        const data = {
          ...rest,
          size,
          width,
          height,
          ...(includeCreatedAt && { createdAt }),
        }

        return onUploadComplete({ uniqueUploadId, data })
      } else {
        onUploadFailed?.({ uniqueUploadId, error })
      }
    },
    [uploadImageFile, conversionOptions, includeCreatedAt, onUploadComplete, onUploadFailed],
  )

  const handleUploadCloudinary = useCallback(
    async ({ file, uniqueUploadId }) => {
      const { data: dataUpload, error } = await uploadCloudinary({ variables: { files: [file] } })

      if (dataUpload) {
        const { getResourceUri, assetsCollection, ...rest } = dataUpload.uploadFileCloudinary
        const data = { ...rest }
        return onUploadComplete({ uniqueUploadId, data })
      } else {
        onUploadFailed?.({ uniqueUploadId, error })
      }
    },
    [uploadCloudinary, onUploadComplete, onUploadFailed],
  )

  const uploadFile = useCallback(
    async file => {
      const { type } = file.file
      const isFile = !type.match('image.*')
      const isSignedUpload = signed || isFile
      const isImage = type.match('image.*')

      const convertedFile = await getFileConverted(file)
      if (isSignedUpload) {
        if (isImage && IMAGE_TYPE_REQUIRING_CONVERSION_OPTIONS.includes(type) && conversionOptions) {
          return await handleUploadImageFile(convertedFile)
        }
        return await handleUploadSigned(convertedFile)
      } else {
        return await handleUploadCloudinary(convertedFile)
      }
    },
    [signed, conversionOptions, handleUploadSigned, handleUploadImageFile, handleUploadCloudinary],
  )

  /**
   * filesRejections: [{ file: File, errors: [{ code: STRING, message: STRING }] }]
   */
  const onDropRejected = useCallback(
    (filesRejections, { forceNotify = false }) => {
      onUploadRejected && onUploadRejected(filesRejections)

      if (!notifyOnReject && !forceNotify) return

      if (!multiple && filesRejections.length > 1) {
        const errorMessage = formatMessage(translations.rejectedMultiple)
        return pushError(errorMessage)
      }

      filesRejections.forEach(({ file, errors }) => {
        const [error] = errors
        switch (true) {
          case error.code === DROPZONE_ERROR_CODE.FILE_INVALID_TYPE: {
            const format = acceptString(accept).replace(/image\/|text\/|application\//gi, '')
            const errorMessage = formatMessage(translations.dropRejectedFileType, { format })
            return pushError(errorMessage)
          }
          case error.code === DROPZONE_ERROR_CODE.FILE_TOO_LARGE: {
            const size = maxSize / 10 ** 6
            const errorMessage = formatMessage(translations.dropRejectedFileSize, { size })
            return pushError(errorMessage)
          }
          default: {
            const errorMessage = formatMessage(translations.dropRejectedUnknown)
            return pushError(errorMessage)
          }
        }
      })
    },
    [notifyOnReject, accept, multiple, maxSize, formatMessage, onUploadRejected, pushError],
  )

  const onDropAccepted = useCallback(
    async files => {
      onUploadStart && onUploadStart(files)
      await Promise.all(files.map(file => uploadFile(file)))
    },
    [onUploadStart, uploadFile],
  )

  const onDrop = useCallback(
    async (acceptedFiles, filesRejection) => {
      if (limit && customErrorMessage && acceptedFiles && acceptedFiles.length > limit) {
        pushError(customErrorMessage(acceptedFiles))
        return
      }
      let copyAcceptedFiles = [...acceptedFiles]
      const copyFilesRejection = [...filesRejection]

      if (customFileValidator) {
        for (const file of acceptedFiles) {
          try {
            await customFileValidator(file, acceptedFiles)
          } catch (e) {
            // match format function onDropRejected(filesRejection) where filesRejection = { file, errors }
            // TODO better identify error
            const fileRejection = { file, errors: [{ message: e.message }] }
            copyFilesRejection.push(fileRejection)
            copyAcceptedFiles.splice(
              copyAcceptedFiles.findIndex(({ path }) => path === file.path),
              1,
            )
            if (includeCustomRejectedFiles) {
              // clean accepted files if customFileValidator is used
              copyAcceptedFiles = []
            }
          }
        }
      }

      if (copyFilesRejection.length) {
        const rejected = includeCustomRejectedFiles ? copyFilesRejection : filesRejection
        onDropRejected(rejected, { forceNotify: copyAcceptedFiles.length > 0 })
      }

      if (copyAcceptedFiles.length) {
        const files = copyAcceptedFiles.map((file, index) => ({
          uniqueUploadId: uniqueUploadId && index === 0 ? uniqueUploadId : `${file.path}-${uniqueId()}`,
          file,
        }))
        onDropAccepted(files)
      }
    },
    [
      uniqueUploadId,
      customFileValidator,
      customErrorMessage,
      limit,
      pushError,
      onDropAccepted,
      onDropRejected,
      includeCustomRejectedFiles,
    ],
  )

  const options = {
    onDrop,
    disabled: disabled || loading,
    multiple,
    maxSize,
    accept,
    noClick,
    ...(maxFiles && { maxFiles }),
  }
  if (getFilesFromEvent) {
    options.getFilesFromEvent = getFilesFromEvent
  }
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone(options)

  return { getRootProps, getInputProps, loading, percent, isDragActive, open }
}

export const TODO_UploadCompleteType = null
export const TODO_UploadAcceptedFileType = null
export default useUploadDrop
